import { Component, computed, inject, input, model, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { BranchService } from '../../../service/branch.service';
import { LocationService } from '../../../service/location.service';
import { NotifyService } from '../../../service/notify.service';
import { PanoptycAdminService } from '../../../service/panoptyc-admin.service';
import { Branch, LocationResponse } from '../../../types';

@Component({
  selector: 'app-branch-split-modal',
  templateUrl: './branch-split-modal.component.html',
  styleUrls: ['./branch-split-modal.component.scss'],
  standalone: false,
})
export class BranchSplitModalComponent implements OnInit {
  private adminService = inject(PanoptycAdminService);
  private notifyService = inject(NotifyService);
  private locationService = inject(LocationService);
  private branchService = inject(BranchService);
  private router = inject(Router);

  public currentBranch = input.required<Branch>();
  public isVisible = model<boolean>(false);

  public locationChoices = signal<LocationResponse[]>([]);
  public branchChoices = signal<Branch[]>([]);
  public isLoading = signal<boolean>(false);
  public isBranchLoading = signal<boolean>(false);
  public targetMode = signal<'new' | 'existing'>('new');
  public newBranchName = signal<string>('');
  public selectedBranchId = signal<number | undefined>(undefined);
  public selectedLocationIds = signal<number[]>([]);

  public selectedBranch = computed(() => this.branchChoices().find((branch) => branch.id === this.selectedBranchId()));
  public targetBranchName = computed(() =>
    this.targetMode() === 'new' ? this.newBranchName().trim() : (this.selectedBranch()?.name ?? ''),
  );
  public canSplit = computed(
    () => this.selectedLocationIds().length > 0 && !!this.targetBranchName() && !this.isLoading() && !this.isBranchLoading(),
  );

  ngOnInit() {
    this.loadBranchLocations();
    this.loadBranches();
  }

  loadBranchLocations() {
    if (!this.currentBranch()) return;
    this.isLoading.set(true);

    this.locationService.getAllLocations({ branch_ids: [this.currentBranch().id], include_kiosks: true }).subscribe({
      next: (locations) => {
        this.locationChoices.set(locations.items);
      },
      complete: () => {
        this.isLoading.set(false);
      },
      error: () => {
        this.notifyService.notify('Error loading locations for branch.', 'Close');
      },
    });
  }

  loadBranches() {
    this.isBranchLoading.set(true);

    this.branchService.getBranches().subscribe({
      next: (branches) => {
        this.branchChoices.set(
          branches.items.filter(
            (branch) => branch.id !== this.currentBranch().id && branch.customer_type === this.currentBranch().customer_type,
          ),
        );
      },
      complete: () => {
        this.isBranchLoading.set(false);
      },
      error: () => {
        this.isBranchLoading.set(false);
        this.notifyService.notify('Error loading branches.', 'Close');
      },
    });
  }

  splitBranch() {
    if (!this.canSplit()) return;

    this.notifyService.notify('Currently splitting branches. You will be redirected when this is completed.');

    const newBranchName = this.targetMode() === 'new' ? this.newBranchName().trim() : undefined;
    const targetBranchId = this.targetMode() === 'existing' ? this.selectedBranchId() : undefined;
    this.adminService.splitBranch(this.currentBranch().id, newBranchName, this.selectedLocationIds(), targetBranchId).subscribe({
      next: () => {
        this.router.navigate(['/home', 'manage', 'branches']);
      },
      error: () => {
        this.notifyService.notify('Error splitting locations into target branch.', 'Close');
      },
    });
  }
}
